import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { t } from "@/lib/strings";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import TrustStrip from "@/components/landing/TrustStrip";
import ResultsPreview from "@/components/landing/ResultsPreview";
import HowItWorks from "@/components/landing/HowItWorks";
import WhyItMatters from "@/components/landing/WhyItMatters";
import VisibilityFlow from "@/components/landing/VisibilityFlow";
import FullReportIncludes from "@/components/landing/FullReportIncludes";
import Testimonials from "@/components/landing/Testimonials";
import FAQSection from "@/components/landing/FAQSection";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import ScrollProgress from "@/components/anim/ScrollProgress";
import AnalyzerOverlay from "@/components/funnel/AnalyzerOverlay";

export default function LandingPage() {
  const [analysis, setAnalysis] = useState(null); // { analysis_id, preview }
  const [scanning, setScanning] = useState(false);
  const [scanDomain, setScanDomain] = useState("");
  const pendingRef = useRef(null);
  const campaign = useRef(new URLSearchParams(window.location.search).get("utm_source") || "organic").current;

  useEffect(() => {
    api.track("page_view", { domain: window.location.hostname, meta: { campaign } });
  }, [campaign]);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => document.querySelector('[data-testid="domain-input"]')?.focus({ preventScroll: true }), 700);
  };

  const handleAnalyze = (domain) => {
    setAnalysis(null);
    setScanDomain(domain);
    pendingRef.current = api.analyze(domain, campaign);
    pendingRef.current.catch(() => {});
    setScanning(true);
  };

  const handleScanDone = useCallback(async () => {
    try {
      const data = await pendingRef.current;
      setAnalysis(data);
      setTimeout(() => {
        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("page.analyzeError"));
    } finally {
      setScanning(false);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white relative">
      <ScrollProgress />
      <Header onCta={scrollTop} />
      <Hero onAnalyze={handleAnalyze} loading={scanning} />
      <TrustStrip />
      {analysis && (
        <ResultsPreview
          key={analysis.analysis_id}
          preview={analysis.preview}
          analysisId={analysis.analysis_id}
          campaign={campaign}
        />
      )}
      <VisibilityFlow />
      <HowItWorks />
      <WhyItMatters />
      <FullReportIncludes onCta={scrollTop} />
      <Testimonials />
      <FAQSection />
      <FinalCTA onAnalyze={handleAnalyze} loading={scanning} />
      <Footer />
      <AnimatePresence>
        {scanning && <AnalyzerOverlay domain={scanDomain} onDone={handleScanDone} />}
      </AnimatePresence>
    </div>
  );
}
